/**
 * Custom Menu Integration for Menus/index.jsx
 *
 * This module provides helper functions to integrate custom menus from whitelabel
 * into the existing menu structure.
 */

/**
 * Convert custom menu format to existing menu format
 *
 * Custom menu format (from whitelabel):
 * {
 *   id: 'menu-id',
 *   name: 'Menu Name',
 *   icon: 'icon/24px/unicon/sun',
 *   path: 'menu/path',
 *   sequence: 1,
 *   children: [...]
 * }
 *
 * Existing menu format:
 * {
 *   id: 'menu-id',
 *   name: 'Menu Name',
 *   icon: SVGComponent,
 *   path: 'menu/path',
 *   sequence: 1,
 *   access: ['ROLE'],
 *   children: [...]
 * }
 */
export const convertCustomMenuToMenuFormat = (
  customMenu,
  defaultAccess = ['SISWA', 'GURU', 'WALIKELAS', 'ORTU', 'ADMIN']
) => {
  const converted = {
    id: customMenu.id,
    name: customMenu.name,
    icon: customMenu.icon, // Keep as string path, will be converted by IconCreate
    path: customMenu.path || null,
    sequence: customMenu.sequence || 0,
    access: customMenu.access || defaultAccess, // Allow custom access or use default
    state: customMenu.state || null,
  };

  // Convert children recursively
  if (customMenu.children && customMenu.children.length > 0) {
    converted.children = customMenu.children.map((child) => convertCustomMenuToMenuFormat(child, defaultAccess));
  }

  return converted;
};

/**
 * Fetch custom menus from a JSON file URL
 *
 * @param {string} url - URL to the JSON file
 * @returns {Promise<Array>} Array of custom menu objects
 */
export const fetchCustomMenusFromUrl = async (url) => {
  try {
    const response = await fetch(url);

    if (!response.ok) {
      console.error(`Failed to fetch custom menus from URL: ${url}, status: ${response.status}`);
      return [];
    }

    const data = await response.json();

    if (!Array.isArray(data)) {
      console.warn('Custom menus data from URL is not an array:', data);
      return [];
    }

    return data;
  } catch (error) {
    console.error('Error fetching custom menus from URL:', error);
    return [];
  }
};

/**
 * Load custom menus from whitelabel configuration (async version)
 * Supports both direct JSON value and file URL
 *
 * @param {Array} whitelabelRoot - Whitelabel data array
 * @returns {Promise<Array>} Array of custom menu objects in menu format
 */
export const loadCustomMenusFromWhitelabelAsync = async (whitelabelRoot) => {
  if (!whitelabelRoot || !Array.isArray(whitelabelRoot)) {
    return [];
  }

  // Find custom_menus key in whitelabel data
  const customMenuData = whitelabelRoot.find((item) => item.key === 'custom_menus');

  if (!customMenuData || !customMenuData.value) {
    return [];
  }

  try {
    let customMenus;

    // Check if value_type is 'file' (URL to JSON file)
    if (customMenuData.value_type === 'file') {
      // Fetch JSON from URL
      customMenus = await fetchCustomMenusFromUrl(customMenuData.value);
    } else {
      // Parse direct JSON value
      customMenus = JSON.parse(customMenuData.value);
    }

    if (!Array.isArray(customMenus)) {
      console.warn('Custom menus value is not an array:', customMenus);
      return [];
    }

    // Convert each custom menu to existing menu format
    return customMenus.map((menu) => convertCustomMenuToMenuFormat(menu));
  } catch (error) {
    console.error('Error loading custom menus from whitelabel:', error);
    return [];
  }
};

/**
 * Load custom menus from whitelabel configuration (legacy sync version)
 * Only supports direct JSON value, not file URLs
 *
 * @param {Array} whitelabelRoot - Whitelabel data array
 * @returns {Array} Array of custom menu objects in menu format
 * @deprecated Use loadCustomMenusFromWhitelabelAsync instead for file URL support
 */
export const loadCustomMenusFromWhitelabel = (whitelabelRoot) => {
  if (!whitelabelRoot || !Array.isArray(whitelabelRoot)) {
    return [];
  }

  // Find custom_menus key in whitelabel data
  const customMenuData = whitelabelRoot.find((item) => item.key === 'custom_menus');

  if (!customMenuData || !customMenuData.value) {
    return [];
  }

  // If value_type is 'file', return empty array (need async version)
  if (customMenuData.value_type === 'file') {
    console.warn(
      'Custom menus with value_type="file" require async loading. Use loadCustomMenusFromWhitelabelAsync instead.'
    );
    return [];
  }

  try {
    // Parse JSON value
    const customMenus = JSON.parse(customMenuData.value);

    if (!Array.isArray(customMenus)) {
      console.warn('Custom menus value is not an array:', customMenus);
      return [];
    }

    // Convert each custom menu to existing menu format
    return customMenus.map((menu) => convertCustomMenuToMenuFormat(menu));
  } catch (error) {
    console.error('Error parsing custom menus from whitelabel:', error);
    return [];
  }
};

/**
 * Merge custom menus with existing menus
 *
 * @param {Array} existingMenus - Existing menu array
 * @param {Array} customMenus - Custom menu array from whitelabel
 * @param {string} position - Where to insert: 'top', 'bottom', 'before-settings', 'after-dashboard'
 * @returns {Array} Merged menu array
 */
export const mergeMenus = (existingMenus, customMenus, position = 'before-settings') => {
  if (!customMenus || customMenus.length === 0) {
    return existingMenus;
  }

  if (position === 'top') {
    // Add custom menus at the top
    return [...customMenus, ...existingMenus];
  }

  if (position === 'bottom') {
    // Add custom menus at the bottom
    return [...existingMenus, ...customMenus];
  }

  if (position === 'before-settings') {
    // Add custom menus before settings (pengaturan)
    const settingsIndex = existingMenus.findIndex((menu) => menu.id === 'pengaturan');
    if (settingsIndex !== -1) {
      return [...existingMenus.slice(0, settingsIndex), ...customMenus, ...existingMenus.slice(settingsIndex)];
    }
    // If settings not found, add at bottom
    return [...existingMenus, ...customMenus];
  }

  if (position === 'after-dashboard') {
    // Add custom menus after dashboard
    const dashboardIndex = existingMenus.findIndex((menu) => menu.id === 'dashboard');
    if (dashboardIndex !== -1) {
      return [
        ...existingMenus.slice(0, dashboardIndex + 1),
        ...customMenus,
        ...existingMenus.slice(dashboardIndex + 1),
      ];
    }
    // If dashboard not found, add at top
    return [...customMenus, ...existingMenus];
  }

  // Default: add at bottom
  return [...existingMenus, ...customMenus];
};

/**
 * Sort menus by sequence number (for wakaf site type)
 *
 * @param {Array} menus - Menu array
 * @returns {Array} Sorted menu array
 */
export const sortMenusBySequence = (menus) =>
  [...menus].sort((a, b) => {
    const seqA = a?.sequence ?? 0;
    const seqB = b?.sequence ?? 0;
    return seqA - seqB;
  });

/**
 * Example custom menu configuration for testing
 */
export const exampleCustomMenus = [
  {
    id: 'rekap-laporan-6-keunggulan',
    name: 'Rekap Laporan 6 Keunggulan',
    icon: 'icon/16px/unicon/file-check-alt',
    sequence: 100, // High sequence to place before settings
    access: ['GURU', 'WALIKELAS', 'ORTU', 'SISWA'],
    children: [
      {
        id: 'laporan-al-quran',
        name: 'Laporan Al Quran',
        icon: 'icon/24px/unicon/sun',
        path: 'laporan/al-quran',
        sequence: 1,
      },
      {
        id: 'laporan-akademik',
        name: 'Laporan Akademik',
        icon: 'icon/24px/unicon/graduation-cap',
        path: 'laporan/akademik',
        sequence: 2,
      },
      {
        id: 'laporan-bahasa',
        name: 'Laporan Bahasa',
        icon: 'icon/24px/unicon/comment-verify',
        path: 'laporan/bahasa',
        sequence: 3,
      },
      {
        id: 'laporan-karakter',
        name: 'Laporan Karakter',
        icon: 'icon/24px/unicon/cell',
        path: 'laporan/karakter',
        sequence: 4,
      },
      {
        id: 'laporan-leadership',
        name: 'Laporan Leadership',
        icon: 'icon/24px/unicon/brightness-plus',
        path: 'laporan/leadership',
        sequence: 5,
      },
      {
        id: 'laporan-digital',
        name: 'Laporan Digital',
        icon: 'icon/24px/unicon/coins',
        path: 'laporan/digital',
        sequence: 6,
      },
    ],
  },
];

export default {
  convertCustomMenuToMenuFormat,
  loadCustomMenusFromWhitelabel,
  loadCustomMenusFromWhitelabelAsync,
  fetchCustomMenusFromUrl,
  mergeMenus,
  sortMenusBySequence,
  exampleCustomMenus,
};
